import React, { useEffect, useState } from 'react';
import SeloDeComentarios from '../../../components/comentarios/SeloDeComentarios';

import Paginacao from '../../../components/paginacao/Paginacao';
import { AreaEstado, CardGrafico, Kpi } from '../../../shared/ui/GraficoBase';
import { formatarMoeda, formatarMoedaCompacta, formatarNumero } from '../../../shared/ui/chart-palette';
import DetalheDoPedidoModal from './DetalheDoPedidoModal';
import EntregasDoFornecedorModal from './EntregasDoFornecedorModal';
import {
  AgrupamentoDePedido,
  CentroDeCusto,
  FiltrosCompras,
  FiltrosDeRecorteDePedido,
  PedidosAgrupados,
  PedidosComOrigem,
  ResumoPedidos,
  SituacaoDaChegada,
  fetchEntregaNoPrazo,
  fetchPedidosAgrupados,
  fetchPedidosComOrigem,
  fetchPedidosResumo,
} from '../../../api/comprasDashboardApi';
import {
  ColunaDoFunil,
  EntregaNoPrazoBloco,
  EvolucaoMensal,
  FaixaDeErro,
  FiltroDeSituacao,
  Fornecedores,
} from './BlocosCompras';
import type { PedidosDoRecorteAberto } from './PedidosDoRecorteModal';
import { blocosDoModo } from '../blocosDoModo';
import '../../../shared/ui/badge-status.css';
import { formatarDataPura, formatarInstante } from '../formatarData';
import '../../../shared/feedback/aviso-de-tela.css';
import {
  OPCOES_DE_CHEGADA,
  SITUACOES_DE_PEDIDO,
  estadosDaChegada,
  rotuloDosStatusDePedido,
} from '../situacoes';

/**
 * A aba **Pedidos** do Dashboard de Compras (at-109; os cards do Painel chegaram na at-121).
 *
 * ## 🔴 De onde vêm comprador, placa, oficina, centro de custo e solicitante
 *
 * **Não vêm do pedido.** O pedido de compra do Nasajon não tem nenhum deles — a listagem devolve
 * fornecedor, valor, situação e datas, e nada mais. Os cinco são da **requisição que o originou**,
 * e o caminho até ela é `pedido → negociação → requisição`, resolvido no servidor.
 *
 * Consequência que a tela precisa dizer: **pedido sem requisição de origem existe** (compra direta,
 * ou negociação ainda fora do espelho — `BUG-019`) e é CONTADO, numa linha própria de cada card.
 *
 * ## A situação do pedido é a de COMPRAS (at-128)
 *
 * Aberto, aguardando documento, parcialmente liquidado, liquidado e cancelado — o
 * `situacao_gerencial` do Nasajon. "Aguardando atendimento", "em cotação" e "atendida" continuam
 * sendo estados da REQUISIÇÃO, anteriores ao pedido, e não aparecem aqui.
 *
 * ## "Entrega no prazo" por pedido (at-125)
 *
 * O bloco lê o elo pedido → nota da API do Nasajon (at-128; antes, o relatório subido à mão):
 * previsão do PEDIDO contra a entrada da última NOTA. A coluna "Chegada" da lista usa a MESMA
 * chegada, pedido a pedido.
 */

const POR_PAGINA = 10;
const POR_PAGINA_DO_CARD = 10;

/** Quem abre a lista de pedidos de um número da aba — a página monta o modal. */
export type AbrirRecorteDePedido = (aberto: PedidosDoRecorteAberto) => void;

interface Carregado<T> {
  dados: T | null;
  carregando: boolean;
  erro: boolean;
}

function useCarga<T>(buscar: () => Promise<T>, deps: unknown[]): Carregado<T> {
  const [estado, setEstado] = useState<Carregado<T>>({ dados: null, carregando: true, erro: false });

  useEffect(() => {
    let cancelado = false;
    // ⚠️ `dados` preservado durante a recarga: a troca de página ou de status de um card não pode
    // apagar a tabela inteira enquanto a próxima página vem.
    setEstado((anterior) => ({ dados: anterior.dados, carregando: true, erro: false }));
    buscar()
      .then((dados) => {
        if (!cancelado) setEstado({ dados, carregando: false, erro: false });
      })
      .catch(() => {
        // 🔴 `dados: null` de propósito: o bloco mostra a faixa de erro e NUNCA zero. "Nenhum
        // pedido no período" é afirmação sobre a operação de Compras (at-60).
        if (!cancelado) setEstado({ dados: null, carregando: false, erro: true });
      });
    return () => {
      cancelado = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return estado;
}

/**
 * O bloco "Entrega no prazo" com a carga dele e o modal do clique (at-125).
 *
 * ⚠️ Componente próprio, montado só no recorte de oficina: a carga vive aqui dentro para que fora
 * desse recorte a rota não seja chamada — bloco desmontado não paga consulta (`blocosDoModo`).
 */
function EntregaNoPrazoDaAba({ filtros }: { filtros: FiltrosCompras }) {
  const estado = useCarga(() => fetchEntregaNoPrazo(filtros), [filtros]);
  const [fornecedorAberto, setFornecedorAberto] = useState<string | null>(null);
  return (
    <>
      <EntregaNoPrazoBloco estado={estado} aoAbrirFornecedor={setFornecedorAberto} />
      {fornecedorAberto ? (
        <EntregasDoFornecedorModal
          fornecedor={fornecedorAberto}
          filtros={filtros}
          onFechar={() => setFornecedorAberto(null)}
        />
      ) : null}
    </>
  );
}

/** Os indicadores do PERÍODO (at-121 — até aqui três deles eram somados sobre a página da lista). */
export function KpisPedidos({
  estado,
  aoAbrir,
}: {
  estado: Carregado<ResumoPedidos>;
  aoAbrir: AbrirRecorteDePedido;
}) {
  if (estado.erro) return <FaixaDeErro oQue="os indicadores de pedidos" />;

  const k = estado.dados?.kpis;
  const carregando = estado.carregando && !k;
  const dias = k?.diasEmAberto;

  return (
    <div className="do-kpis">
      <Kpi
        rotulo="Total de pedidos"
        valor={k ? formatarNumero(k.totalPedidos) : ''}
        apoio="emitidos no período, sem os cancelados"
        carregando={carregando}
        detalheRotulo="Ver todos os pedidos do período"
        onDetalhar={() =>
          aoAbrir({ titulo: 'Total de pedidos', legenda: 'cancelados não entram na conta' })
        }
      />
      {/* at-122: "Itens" ao lado de "Total de pedidos" (pedido do Victor, 06/10/2026). */}
      <Kpi
        rotulo="Itens"
        valor={k ? formatarNumero(k.itens.total) : ''}
        apoio={
          k
            ? `itens sincronizados em ${formatarNumero(k.itens.cobertura.comItens)} de ${formatarNumero(k.itens.cobertura.total)} pedidos`
            : undefined
        }
        carregando={carregando}
        // 🔴 Zero itens sincronizados não é "zero itens comprados": a carga histórica dos itens
        // pode não ter rodado (at-109). O número não aparece enquanto não há o que contar.
        indisponivel={k && k.itens.cobertura.comItens === 0 ? 'itens não sincronizados' : undefined}
      />
      {/*
        ⚠️ Valor COMPACTO no lugar do número (pedido do Victor, 06/10/2026: "não quero quebrando em
        duas linhas"): o cartão é estreito e "R$ 603.423,02" quebrava. O valor exato não some —
        vai para a linha de apoio. Mesmo arranjo do KPI de valor protegido do Contac.
      */}
      <Kpi
        rotulo="Valor total"
        valor={k ? formatarMoedaCompacta(k.valorTotal) : ''}
        apoio={k ? `${formatarMoeda(k.valorTotal)} · soma dos pedidos acima` : 'soma dos pedidos acima'}
        carregando={carregando}
      />
      <Kpi
        rotulo="Abertos"
        valor={k ? formatarNumero(k.abertos) : ''}
        apoio='situação "aberto" no Nasajon'
        carregando={carregando}
        detalheRotulo="Ver os pedidos abertos"
        onDetalhar={() =>
          aoAbrir({ titulo: 'Pedidos abertos', filtrosExtras: { situacoes: ['aberto'] } })
        }
      />
      {/*
        at-128: as situações de Compras (aguardando documento, parcialmente liquidado, liquidado)
        estão no Fluxo de pedidos, logo abaixo — repeti-las aqui só alongaria a fileira.
      */}
      <Kpi
        rotulo="Dias em aberto"
        valor={dias?.mediana != null ? `${dias.mediana} d` : ''}
        apoio={
          dias ? `mediana · 90% em até ${dias.p90 ?? 0} d · máx. ${dias.max ?? 0} d` : 'mediana'
        }
        hint="Da emissão do pedido até hoje, só dos pedidos que ainda estão abertos."
        carregando={carregando}
        // 🔴 Sem pedido aberto o número NÃO EXISTE — "0 d" seria lido como "abertos hoje".
        indisponivel={k && !dias ? 'nenhum pedido aberto' : undefined}
        detalheRotulo="Ver os pedidos sobre os quais a mediana é calculada"
        onDetalhar={
          dias
            ? () =>
                aoAbrir({
                  titulo: 'Pedidos abertos',
                  legenda: 'a mediana do card é calculada sobre estes',
                  filtrosExtras: { situacoes: ['aberto'] },
                })
            : undefined
        }
      />
      {/*
        ⚠️ "Abertos com previsão vencida" SAIU (decisão do Victor, 06/10/2026): sem saber se o
        pedido chegou (BUG-009 a BUG-012), o número exigia uma ressalva fixa na aba para não ser
        lido como atraso. Volta quando a Nasajon expuser o elo nota fiscal → pedido. O backend
        continua calculando `abertosComPrevisaoVencida`.
      */}
    </div>
  );
}

/** O fluxo dos pedidos: o que o ERP tem, e nada além (at-121). */
export function FunilPedidos({
  estado,
  aoAbrir,
}: {
  estado: Carregado<ResumoPedidos>;
  aoAbrir: AbrirRecorteDePedido;
}) {
  if (estado.erro) return <FaixaDeErro oQue="o fluxo de pedidos" />;

  // at-128: o funil é a situação de Compras, como o servidor a manda.
  const funil = estado.dados?.funil ?? [];
  const maior = Math.max(1, ...funil.map((l) => l.total));

  return (
    <CardGrafico
      titulo="Fluxo de pedidos"
      descricao="As situações que Compras usa para o pedido no Nasajon. Os cancelados não entram no total."
    >
      {funil.length === 0 ? (
        <AreaEstado carregando={estado.carregando} mensagem="Sem pedidos no período e nos centros escolhidos." />
      ) : (
        <div className="compras-funil">
          {funil.map((linha) => (
            <ColunaDoFunil
              key={linha.estado}
              rotulo={linha.rotulo}
              total={linha.total}
              maior={maior}
              legenda={linha.legenda}
              descricaoDoClique={`Ver os pedidos em ${linha.rotulo.toLowerCase()}`}
              onClick={() =>
                aoAbrir({
                  titulo: `Pedidos — ${linha.rotulo}`,
                  legenda: linha.legenda,
                  filtrosExtras: { situacoes: linha.situacoes },
                })
              }
            />
          ))}
        </div>
      )}
    </CardGrafico>
  );
}

/** O que cada card de agrupamento é. */
const CARDS: Record<
  AgrupamentoDePedido,
  { titulo: string; descricao: string; coluna: string; rotuloDoRecorte: string }
> = {
  comprador: {
    titulo: 'Pedidos por comprador',
    descricao: 'O comprador é o da requisição que originou o pedido.',
    coluna: 'Comprador',
    rotuloDoRecorte: 'comprador',
  },
  centro_custo: {
    titulo: 'Pedidos por centro de custo',
    descricao: 'O centro de custo vem da requisição de origem. A soma das linhas é o total do período.',
    coluna: 'Centro de custo',
    rotuloDoRecorte: 'centro de custo',
  },
  oficina: {
    titulo: 'Pedidos por oficina',
    descricao: 'Só as compras de peça. A oficina vem do texto da requisição de origem.',
    coluna: 'Oficina',
    rotuloDoRecorte: 'oficina',
  },
  placa: {
    titulo: 'Pedidos por placa',
    descricao: 'O que foi comprado para cada veículo. A placa vem do texto da requisição de origem.',
    coluna: 'Placa',
    rotuloDoRecorte: 'placa',
  },
  solicitante: {
    titulo: 'Pedidos por solicitante',
    descricao: 'Quem pediu a compra, na requisição de origem.',
    coluna: 'Solicitante',
    rotuloDoRecorte: 'solicitante',
  },
};

/** O filtro que o clique numa linha do card envia à lista — o mesmo atributo que a contou. */
function filtroDaLinha(por: AgrupamentoDePedido, chave: string): FiltrosDeRecorteDePedido {
  switch (por) {
    case 'placa':
      return { placa: chave };
    case 'oficina':
      return { oficina: chave };
    case 'comprador':
      return { comprador: chave };
    case 'solicitante':
      return { solicitante: chave };
    case 'centro_custo':
      return { centrosCusto: [chave] };
    default:
      return {};
  }
}

/**
 * Um card de agrupamento de pedidos — os cinco da aba saem daqui (at-121).
 *
 * 🔴 As linhas fecham com o total: o que não tem o atributo aparece abaixo da tabela, em duas
 * linhas separadas ("Sem requisição de origem" e "Sem <atributo>"), e cada uma abre a sua lista
 * quando há como filtrá-la.
 *
 * ⚠️ A busca e a paginação são do SERVIDOR (lição do `BUG-014`): busca local só enxergaria a
 * página que veio.
 */
export function PedidosAgrupadosBloco({
  por,
  filtros,
  aoAbrir,
  comFiltroDeStatus = false,
  comBusca = false,
  mostrarItens = false,
}: {
  por: AgrupamentoDePedido;
  filtros: FiltrosCompras;
  aoAbrir: AbrirRecorteDePedido;
  comFiltroDeStatus?: boolean;
  comBusca?: boolean;
  /** Só quando há item sincronizado no período — senão a coluna seria uma fileira de zeros. */
  mostrarItens?: boolean;
}) {
  const card = CARDS[por];
  const [situacoes, setSituacoes] = useState<string[]>([]);
  const [texto, setTexto] = useState('');
  const [busca, setBusca] = useState('');
  const [pagina, setPagina] = useState(1);

  // Trocar o filtro do painel, o status ou a busca reconta tudo — a página 3 do recorte anterior
  // não existe mais.
  useEffect(() => setPagina(1), [filtros, situacoes, busca]);
  useEffect(() => {
    if (!comBusca) return;
    const id = setTimeout(() => setBusca(texto.trim()), 300);
    return () => clearTimeout(id);
  }, [texto, comBusca]);

  const estado = useCarga<PedidosAgrupados>(
    () =>
      fetchPedidosAgrupados({
        ...filtros,
        por,
        situacoes,
        busca: busca || undefined,
        pagina,
        limite: POR_PAGINA_DO_CARD,
      }),
    [filtros, por, situacoes, busca, pagina],
  );

  if (estado.erro) return <FaixaDeErro oQue={`os pedidos por ${card.rotuloDoRecorte}`} />;

  const d = estado.dados;
  const grupos = d?.grupos ?? [];
  const pct =
    d && d.cobertura.total > 0 ? Math.round((d.cobertura.comAtributo / d.cobertura.total) * 100) : 0;
  const totalPaginas = d ? Math.max(1, Math.ceil(d.total / Math.max(1, d.porPagina))) : 1;
  const comStatus = (extra: FiltrosDeRecorteDePedido): FiltrosDeRecorteDePedido =>
    situacoes.length ? { ...extra, situacoes } : extra;
  const legenda = rotuloDosStatusDePedido(situacoes);
  const algumForaDaLista = grupos.some((g) => g.foraDaLista > 0);

  return (
    <CardGrafico titulo={card.titulo} descricao={card.descricao}>
      {d?.origemNaoSincronizada ? (
        /*
         * 🔴 "0 de 717 (0%)" sem explicação foi lido como "nenhum pedido tem placa" no primeiro
         * teste manual (21/09/2026). O que houve foi a camada de negociações não ter rodado.
         */
        <p className="erro-de-tela">
          A origem dos pedidos ainda não foi sincronizada com o Nasajon, então nenhum pedido tem{' '}
          {card.rotuloDoRecorte} por enquanto. Isto não é ausência de dado — é a sincronização que
          ainda não rodou. Confira na aba Sincronização.
        </p>
      ) : d ? (
        // 🔴 Cobertura obrigatória: o atributo vem da requisição de origem.
        //
        // ⚠️ Construção sem concordância de gênero ("Comprador identificada" saiu assim no
        // primeiro teste da at-109): o rótulo entra como complemento, nunca como sujeito.
        <p className="aviso-de-tela">
          Identificação de {card.rotuloDoRecorte}: {formatarNumero(d.cobertura.comAtributo)} de{' '}
          {formatarNumero(d.cobertura.total)} pedidos ({pct}%)
          {legenda ? `, com ${legenda}` : ''}.
        </p>
      ) : null}

      {d?.universoNaoAplicado ? (
        <p className="aviso-de-tela">
          O cadastro de classificações financeiras ainda não foi sincronizado, então este card não
          consegue separar as compras de peça e mostra os pedidos de todas as naturezas.
        </p>
      ) : null}

      {comFiltroDeStatus || comBusca ? (
        <div className="compras-filtros-linha">
          {comFiltroDeStatus ? (
            <FiltroDeSituacao
              valores={situacoes}
              onMudar={setSituacoes}
              idDoCampo={`filtro-status-pedidos-${por}`}
              opcoes={SITUACOES_DE_PEDIDO}
              tituloDoModal="Status dos pedidos"
            />
          ) : null}
          {comBusca ? (
            <div className="compras-filtro-card">
              <label htmlFor={`busca-pedidos-${por}`}>{card.coluna}</label>
              <input
                id={`busca-pedidos-${por}`}
                type="text"
                className="premium-text-input"
                placeholder={`Buscar ${card.rotuloDoRecorte}`}
                value={texto}
                onChange={(e) => setTexto(e.target.value)}
              />
            </div>
          ) : null}
        </div>
      ) : null}

      {grupos.length === 0 ? (
        <AreaEstado
          carregando={estado.carregando}
          mensagem={
            busca
              ? `Nenhum ${card.rotuloDoRecorte} com esse trecho.`
              : `Nenhum pedido com ${card.rotuloDoRecorte} identificado no período e nos centros escolhidos.`
          }
        />
      ) : (
        <div className="do-tabela-wrapper">
          <table className="do-tabela do-tabela--cabe">
            <colgroup>
              {/* O nome fica com 40%; as numéricas dividem o resto, sem o vão no meio da linha. */}
              <col style={{ width: '40%' }} />
              {por === 'centro_custo' ? <col /> : null}
              <col />
              <col />
              {mostrarItens ? <col /> : null}
            </colgroup>
            <thead>
              <tr>
                <th>{card.coluna}</th>
                {por === 'centro_custo' ? <th>Código</th> : null}
                <th>Pedidos</th>
                <th>Valor</th>
                {mostrarItens ? <th>Itens</th> : null}
              </tr>
            </thead>
            <tbody>
              {grupos.map((g) => (
                <tr key={g.chave}>
                  <td title={g.grupo}>
                    <button
                      type="button"
                      className="do-celula-link"
                      onClick={() =>
                        aoAbrir({
                          titulo: `Pedidos de ${g.grupo}`,
                          legenda,
                          filtrosExtras: comStatus(filtroDaLinha(por, g.chave)),
                        })
                      }
                    >
                      {g.grupo}
                    </button>
                    {g.foraDaLista > 0 ? '*' : ''}
                    {/*
                      at-90: quantos pedidos do grupo têm comentário. Zero não desenha nada.
                      Inline, ao lado do nome: preso à direita ele cobria Valor e Itens, porque
                      o `colgroup` fixo do `do-tabela--cabe` ignora o recuo da última célula.
                    */}
                    <SeloDeComentarios
                      total={g.comComentarios}
                      rotuloDe={rotuloDosComentados}
                      posicao="inline"
                    />
                  </td>
                  {por === 'centro_custo' ? <td>{g.codigo ?? '—'}</td> : null}
                  <td>{formatarNumero(g.pedidos)}</td>
                  <td>{formatarMoeda(g.valor)}</td>
                  {mostrarItens ? <td>{formatarNumero(g.itens)}</td> : null}
                </tr>
              ))}
            </tbody>
          </table>
          {totalPaginas > 1 ? (
            <Paginacao paginaAtual={pagina} totalPaginas={totalPaginas} onMudarPagina={setPagina} />
          ) : null}
        </div>
      )}

      {d && !busca && (d.semOrigem.pedidos > 0 || d.semAtributo.pedidos > 0) ? (
        /*
         * 🔴 Os dois resíduos, sempre que existem: são eles que fazem as linhas fecharem com o
         * total. Cada um abre a sua lista quando há como filtrá-la — e quando não há, o número
         * aparece sem link, em vez de abrir uma lista que não o soma.
         */
        <p className="do-nota-janela">
          Fora das linhas:{' '}
          {d.semOrigem.pedidos > 0 ? (
            <>
              <button
                type="button"
                className="dc-link-residuo"
                onClick={() =>
                  aoAbrir({
                    titulo: d.semOrigem.rotulo,
                    legenda: 'compra direta, ou negociação ainda não sincronizada',
                    filtrosExtras: comStatus({ origem: 'sem' }),
                  })
                }
              >
                {formatarNumero(d.semOrigem.pedidos)}
              </button>
              {` ${d.semOrigem.rotulo.toLowerCase()}`}
              <SeloDeComentarios total={d.semOrigem.comComentarios} rotuloDe={rotuloDosComentados} posicao="inline" />
            </>
          ) : null}
          {d.semOrigem.pedidos > 0 && d.semAtributo.pedidos > 0 ? ' e ' : null}
          {d.semAtributo.pedidos > 0 ? (
            <>
              {d.semAtributo.chave ? (
                <button
                  type="button"
                  className="dc-link-residuo"
                  onClick={() =>
                    aoAbrir({
                      titulo: d.semAtributo.rotulo,
                      legenda,
                      filtrosExtras: comStatus(filtroDaLinha(por, d.semAtributo.chave!)),
                    })
                  }
                >
                  {formatarNumero(d.semAtributo.pedidos)}
                </button>
              ) : (
                formatarNumero(d.semAtributo.pedidos)
              )}
              {` ${d.semAtributo.rotulo.toLowerCase()}`}
              <SeloDeComentarios total={d.semAtributo.comComentarios} rotuloDe={rotuloDosComentados} posicao="inline" />
            </>
          ) : null}
          .
        </p>
      ) : null}

      {algumForaDaLista ? (
        <p className="do-nota-janela">
          * Inclui pedidos de requisições cuja classificação financeira não está entre as de compra
          de peça, mas que informam a oficina no texto.
        </p>
      ) : null}
    </CardGrafico>
  );
}

/** O `title` do selo nas linhas de grupo: o número conta PEDIDOS com comentário (at-90). */
const rotuloDosComentados = (n: number) =>
  n === 1 ? '1 pedido com comentário' : `${n} pedidos com comentário`;

/** A lista de pedidos, com a origem em cada linha. */
export function ListaDePedidos({
  filtros,
  centros,
}: {
  filtros: FiltrosCompras;
  centros?: CentroDeCusto[];
}) {
  const [pagina, setPagina] = useState(1);
  /*
   * at-110: o pedido aberto em detalhe.
   *
   * ⚠️ Aqui o hospedeiro é um CARD, não um modal — não há guarda de "clicou fora" a travar.
   */
  const [pedidoAberto, setPedidoAberto] = useState<{ id: string; numero: string | null } | null>(
    null,
  );
  // at-111: as colunas de veículo só existem onde a peça é o assunto.
  const mostraVeiculo = blocosDoModo(filtros.centrosCusto, centros).cardsDePeca;
  const estado = useCarga<PedidosComOrigem>(
    () => fetchPedidosComOrigem({ ...filtros, pagina, limite: POR_PAGINA }),
    [filtros, pagina],
  );

  // Trocar o filtro reconta tudo — a página 3 do recorte anterior não existe mais.
  useEffect(() => setPagina(1), [filtros]);

  if (estado.erro) return <FaixaDeErro oQue="a lista de pedidos" />;

  const d = estado.dados;
  const pedidos = d?.pedidos ?? [];
  const totalPaginas = Math.max(1, Math.ceil((d?.total ?? 0) / POR_PAGINA));

  return (
    <CardGrafico
      titulo="Pedidos"
      descricao="Emissão, fornecedor e valor, com a requisição de origem e o veículo a que ela se refere."
    >
      {d?.centroCustoNaoAplicado ? (
        /*
         * at-112: o centro de custo do pedido vem da REQUISIÇÃO que o originou. Sem o cadastro de
         * locais de uso sincronizado não há como resolvê-lo.
         */
        <p className="aviso-de-tela">
          O filtro de centro de custo ainda não vale para pedidos: o cadastro de locais de uso, que
          diz a qual centro cada requisição pertence, ainda não foi sincronizado. A lista abaixo é
          de todos os centros.
        </p>
      ) : null}

      {d?.origemNaoSincronizada ? (
        <p className="erro-de-tela">
          A origem dos pedidos ainda não foi sincronizada com o Nasajon: as colunas Requisição,
          Placa, Oficina e Comprador ficam vazias até a primeira sincronização de negociações
          rodar. Confira na aba Sincronização.
        </p>
      ) : d && d.semOrigemNaPagina > 0 ? (
        <p className="aviso-de-tela">
          {formatarNumero(d.semOrigemNaPagina)} de {formatarNumero(d.cobertura.naPagina)} pedidos
          desta página não têm requisição de origem no sistema. Eles aparecem na lista sem
          comprador, placa e oficina.
        </p>
      ) : null}

      {pedidos.length === 0 ? (
        <AreaEstado
          carregando={estado.carregando}
          mensagem="Sem pedidos no período e no setor escolhidos."
        />
      ) : (
        <div className="do-tabela-wrapper">
          <table className="do-tabela">
            <thead>
              <tr>
                <th>Nº</th>
                <th>Emissão</th>
                <th>Fornecedor</th>
                <th>Requisição</th>
                {/*
                  at-111 — em "Outros setores" as duas colunas não são RENDERIZADAS: compra de
                  Marketing ou RH não tem veículo nem oficina.
                */}
                {mostraVeiculo ? <th>Placa</th> : null}
                {mostraVeiculo ? <th>Oficina</th> : null}
                <th>Comprador</th>
                <th>Itens</th>
                <th>Valor</th>
                <th>Previsão</th>
                <th>Chegada</th>
              </tr>
            </thead>
            <tbody>
              {pedidos.map((p) => (
                <tr key={p.id}>
                  <td>
                    <button
                      type="button"
                      className="do-celula-link"
                      onClick={() => setPedidoAberto({ id: p.id, numero: p.numero })}
                    >
                      {p.numero ?? 'sem número'}
                    </button>
                    <SeloDeComentarios total={p.comentarios} />
                  </td>
                  {/*
                    🔴 Emissão e previsão do pedido são datas PURAS (0 de 10.058 com hora, medido em
                    produção em 06/10/2026). `new Date` as mostrava um dia antes no fuso da oficina.
                  */}
                  <td>{p.emissao ? formatarDataPura(p.emissao) : '—'}</td>
                  <td title={p.fornecedorNome ?? undefined}>{p.fornecedorNome ?? '—'}</td>
                  <td>{p.requisicaoCodigo ?? '—'}</td>
                  {mostraVeiculo ? <td>{p.placa ?? '—'}</td> : null}
                  {mostraVeiculo ? (
                    <td title={p.oficina ?? undefined}>{p.oficina ?? '—'}</td>
                  ) : null}
                  <td title={p.compradorNome ?? undefined}>{p.compradorNome ?? '—'}</td>
                  <td>{formatarNumero(p.itens)}</td>
                  <td>{formatarMoeda(p.total)}</td>
                  <td title="O prazo de entrega registrado no pedido.">
                    {p.previsaoDeEntrega
                      ? formatarDataPura(p.previsaoDeEntrega)
                      : 'Sem previsão'}
                  </td>
                  {/*
                    at-128: a chegada pela nota de entrada (elo da API), contra a previsão. Volta a
                    existir porque o elo pedido → nota passou a existir (decisão do Victor,
                    22/10/2026); sem ele, a marca se lia como atraso (06/10/2026).
                  */}
                  <td>
                    <SeloDaChegada
                      situacao={p.situacaoChegada}
                      entregueEm={p.entregueEm}
                      notas={p.notasDeEntrada}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {totalPaginas > 1 ? (
            <Paginacao paginaAtual={pagina} totalPaginas={totalPaginas} onMudarPagina={setPagina} />
          ) : null}
        </div>
      )}

      {pedidoAberto && (
        <DetalheDoPedidoModal
          pedidoId={pedidoAberto.id}
          numero={pedidoAberto.numero}
          onFechar={() => setPedidoAberto(null)}
        />
      )}
    </CardGrafico>
  );
}

/**
 * A chegada de um pedido contra a previsão dele (at-128) — o selo da coluna "Chegada".
 *
 * 🔴 "Previsão vencida" é LARANJA e não diz "atrasado": sem nota de entrada, a peça pode ter ido
 * direto para a oficina externa, que nunca entra no ERP. Só quem CHEGOU depois da previsão é
 * "atrasado" — e ainda assim laranja, porque é fato, não falha do sistema.
 */
export function SeloDaChegada({
  situacao,
  entregueEm,
  notas,
}: {
  situacao: SituacaoDaChegada;
  entregueEm: string | null;
  notas: number;
}) {
  const titulo = entregueEm ? `${notas} nota(s) de entrada; vale a data da última.` : undefined;
  switch (situacao) {
    case 'CHEGOU_NO_PRAZO':
      return (
        <span className="cilia-badge badge-verde" title={titulo}>
          No prazo, em {formatarDataPura(entregueEm)}
        </span>
      );
    case 'CHEGOU_ATRASADO':
      return (
        <span className="cilia-badge badge-laranja" title={titulo}>
          Atrasado, em {formatarDataPura(entregueEm)}
        </span>
      );
    case 'CHEGOU_SEM_PREVISAO':
      return (
        <span className="cilia-badge badge-azul" title={titulo}>
          Chegou em {formatarDataPura(entregueEm)}
        </span>
      );
    case 'PREVISAO_VENCIDA':
      return (
        <span
          className="cilia-badge badge-laranja"
          title="A previsão passou sem nota de entrada. A peça pode ter ido direto para a oficina externa, que não gera nota."
        >
          Previsão vencida
        </span>
      );
    default:
      return <span className="cilia-badge badge-cinza">Aguardando</span>;
  }
}

/**
 * Acompanhamento de entrega (emergencial, pedido do Victor, 20/10/2026): pedido a pedido, a
 * previsão registrada no pedido e se ele já chegou.
 *
 * at-128: a chegada vem do elo pedido → nota da API do Nasajon (era o relatório subido à mão), e a
 * coluna marca no prazo / atrasado (decisão do Victor, 22/10/2026, revertendo a de 06/10, que só
 * existia porque o elo faltava). A tela continua sem dizer "não entregue": diz "aguardando" ou
 * "previsão vencida", porque a peça de oficina externa não gera nota.
 */
export function AcompanhamentoDeEntrega({
  filtros,
  centros,
}: {
  filtros: FiltrosCompras;
  centros?: CentroDeCusto[];
}) {
  const [pagina, setPagina] = useState(1);
  /*
   * at-129 — o filtro é pelos estados da coluna "Chegada" (decisão do Victor, 22/10/2026). Abre em
   * "Aguardando", o mais próximo do padrão anterior ("Sem nota de entrada"), mas agora SEM os de
   * previsão vencida, que têm opção própria. Nada marcado = todos, como nos outros filtros.
   */
  const [chegada, setChegada] = useState<string[]>(['AGUARDANDO']);
  const [pedidoAberto, setPedidoAberto] = useState<{ id: string; numero: string | null } | null>(
    null,
  );
  const mostraVeiculo = blocosDoModo(filtros.centrosCusto, centros).cardsDePeca;
  const estado = useCarga<PedidosComOrigem>(
    () =>
      fetchPedidosComOrigem({
        ...filtros,
        ...(chegada.length ? { chegada: estadosDaChegada(chegada) as SituacaoDaChegada[] } : {}),
        pagina,
        limite: POR_PAGINA,
      }),
    [filtros, chegada, pagina],
  );

  useEffect(() => setPagina(1), [filtros, chegada]);

  if (estado.erro) return <FaixaDeErro oQue="o acompanhamento de entrega" />;

  const d = estado.dados;
  const pedidos = d?.pedidos ?? [];
  const totalPaginas = Math.max(1, Math.ceil((d?.total ?? 0) / POR_PAGINA));
  const sincronizacao = d?.sincronizacao;

  return (
    <CardGrafico
      titulo="Acompanhamento de entrega"
      descricao="Pedido a pedido: a previsão de entrega registrada e se o pedido já chegou, no prazo ou atrasado."
    >
      {sincronizacao ? (
        <p className="aviso-de-tela">
          {sincronizacao.ultimoSucessoEm
            ? `A chegada vem do Nasajon, sincronizada em ${formatarInstante(sincronizacao.ultimoSucessoEm)}. `
            : 'A chegada dos pedidos ainda não foi sincronizada com o Nasajon, então nenhum pedido aparece como entregue. '}
          Pedido sem nota de entrada pode ter chegado depois disso, ou ido direto para a oficina
          externa, que não gera nota.
        </p>
      ) : null}

      <div className="compras-filtros-linha">
        <FiltroDeSituacao
          valores={chegada}
          onMudar={setChegada}
          idDoCampo="filtro-acompanhamento-chegada"
          opcoes={OPCOES_DE_CHEGADA}
          tituloDoModal="Chegada dos pedidos"
          rotulo="Chegada (só deste card)"
        />
      </div>

      {pedidos.length === 0 ? (
        <AreaEstado
          carregando={estado.carregando}
          mensagem="Nenhum pedido nesta situação, no período e nos centros escolhidos."
        />
      ) : (
        <div className="do-tabela-wrapper">
          <table className="do-tabela">
            <thead>
              <tr>
                <th>Pedido</th>
                <th>Emissão</th>
                <th>Fornecedor</th>
                <th>Comprador</th>
                {mostraVeiculo ? <th>Placa</th> : null}
                <th>Valor</th>
                <th>Previsão de entrega</th>
                <th>Chegada</th>
              </tr>
            </thead>
            <tbody>
              {pedidos.map((p) => (
                <tr key={p.id}>
                  <td>
                    <button
                      type="button"
                      className="do-celula-link"
                      onClick={() => setPedidoAberto({ id: p.id, numero: p.numero })}
                    >
                      {p.numero ?? 'sem número'}
                    </button>
                    <SeloDeComentarios total={p.comentarios} />
                  </td>
                  <td>{p.emissao ? formatarDataPura(p.emissao) : '—'}</td>
                  <td title={p.fornecedorNome ?? undefined}>{p.fornecedorNome ?? '—'}</td>
                  <td title={p.compradorNome ?? undefined}>
                    {p.compradorNome ?? (p.requisicaoCodigo ? 'sem comprador' : 'sem requisição')}
                  </td>
                  {mostraVeiculo ? <td>{p.placa ?? '—'}</td> : null}
                  <td>{formatarMoeda(p.total)}</td>
                  <td>{p.previsaoDeEntrega ? formatarDataPura(p.previsaoDeEntrega) : 'Sem previsão'}</td>
                  <td>
                    <SeloDaChegada
                      situacao={p.situacaoChegada}
                      entregueEm={p.entregueEm}
                      notas={p.notasDeEntrada}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {totalPaginas > 1 ? (
            <Paginacao paginaAtual={pagina} totalPaginas={totalPaginas} onMudarPagina={setPagina} />
          ) : null}
        </div>
      )}

      {pedidoAberto && (
        <DetalheDoPedidoModal
          pedidoId={pedidoAberto.id}
          numero={pedidoAberto.numero}
          onFechar={() => setPedidoAberto(null)}
        />
      )}
    </CardGrafico>
  );
}

/** A aba inteira — é o que a página monta. */
export default function AbaPedidos({
  filtros,
  centros,
  fornecedores,
  aoAbrirFornecedor,
  evolucao,
  aoAbrirMes,
  aoAbrirRecorte,
}: {
  filtros: FiltrosCompras;
  centros?: CentroDeCusto[];
  /** O card de fornecedores, que vive aqui desde a at-112: ele conta pedidos, não requisições. */
  fornecedores: Parameters<typeof Fornecedores>[0]['estado'];
  aoAbrirFornecedor?: (f: { pk: string | null; nome: string | null }) => void;
  /** A evolução mensal, que vive aqui desde a at-112, pelo mesmo motivo. */
  evolucao: Parameters<typeof EvolucaoMensal>[0]['estado'];
  aoAbrirMes: (mes: string) => void;
  /** at-121: o clique em qualquer número da aba abre a lista de pedidos que ele conta. */
  aoAbrirRecorte: AbrirRecorteDePedido;
}) {
  const resumo = useCarga<ResumoPedidos>(() => fetchPedidosResumo(filtros), [filtros]);
  /*
   * 🔴 A MESMA regra do Painel (`blocosDoModo`): oficina e placa só onde a peça é o assunto;
   * solicitante fora do recorte "só oficina". Montagem condicional, nunca `display: none` — bloco
   * escondido continuaria pedindo a carga dele.
   */
  const blocos = blocosDoModo(filtros.centrosCusto, centros);
  const mostrarItens = (resumo.dados?.kpis.itens.cobertura.comItens ?? 0) > 0;

  return (
    <>
      {/*
        ⚠️ A faixa fixa "o sistema não sabe dizer se um pedido chegou" SAIU (decisão do Victor,
        06/10/2026), junto do card e do selo de previsão vencida que ela existia para explicar. A
        explicação segue no drawer "Como Funciona".
      */}
      <KpisPedidos estado={resumo} aoAbrir={aoAbrirRecorte} />

      <div className="do-grid-largo">
        <FunilPedidos estado={resumo} aoAbrir={aoAbrirRecorte} />
      </div>

      <div className="do-grid-largo">
        <EvolucaoMensal estado={evolucao} aoAbrirMes={aoAbrirMes} />
      </div>

      <div className="do-grid-largo">
        <Fornecedores estado={fornecedores} aoAbrirFornecedor={aoAbrirFornecedor} />
      </div>

      <div className="do-grid-largo">
        <PedidosAgrupadosBloco
          por="comprador"
          filtros={filtros}
          aoAbrir={aoAbrirRecorte}
          comFiltroDeStatus
          mostrarItens={mostrarItens}
        />
      </div>

      {/*
        at-129 — a ordem do fim da aba (decisão do Victor, 22/10/2026): Placa, Solicitante e Centro
        de custo descem para logo acima da lista de pedidos, nessa ordem — a mesma da aba
        Requisições. Oficina continua no alto, junto dos blocos de entrega, que falam de peça.
        ⚠️ Placa e Oficina deixaram de ser irmãs no mesmo fragmento: cada uma tem o seu
        condicional (`cardsDePeca`), e a montagem continua condicional, nunca `display: none`.
      */}
      {blocos.cardsDePeca ? (
        <div className="do-grid-largo">
          <PedidosAgrupadosBloco
            por="oficina"
            filtros={filtros}
            aoAbrir={aoAbrirRecorte}
            comBusca
            mostrarItens={mostrarItens}
          />
        </div>
      ) : null}

      {/*
        at-119 — "Entrega no prazo" veio do Painel: ele fala de PEDIDO de compra. Montado só no
        recorte que inclui centro de oficina. Desde a at-125 volta a mostrar número; desde a
        at-128 pela API do Nasajon.
      */}
      {blocos.cardsDePeca ? (
        <div className="do-grid-largo">
          <EntregaNoPrazoDaAba filtros={filtros} />
        </div>
      ) : null}

      <div className="do-grid-largo">
        <AcompanhamentoDeEntrega filtros={filtros} centros={centros} />
      </div>

      {blocos.cardsDePeca ? (
        <div className="do-grid-largo">
          <PedidosAgrupadosBloco
            por="placa"
            filtros={filtros}
            aoAbrir={aoAbrirRecorte}
            comFiltroDeStatus
            comBusca
            mostrarItens={mostrarItens}
          />
        </div>
      ) : null}

      {blocos.solicitantes ? (
        <div className="do-grid-largo">
          <PedidosAgrupadosBloco
            por="solicitante"
            filtros={filtros}
            aoAbrir={aoAbrirRecorte}
            mostrarItens={mostrarItens}
          />
        </div>
      ) : null}

      <div className="do-grid-largo">
        <PedidosAgrupadosBloco
          por="centro_custo"
          filtros={filtros}
          aoAbrir={aoAbrirRecorte}
          mostrarItens={mostrarItens}
        />
      </div>

      <div className="do-grid-largo">
        <ListaDePedidos filtros={filtros} centros={centros} />
      </div>
    </>
  );
}
